import Croppie, { CroppieOptions } from 'croppie';

// Type definitions
export interface ImageProcessingOptions {
  maxWidth: number;
  maxHeight: number;
  quality?: number;
  format?: 'png' | 'jpeg';
}

export interface CroppieConfig {
  containerWidth: number;
  aspectRatio: number;
  type: 'circle' | 'square';
  enableZoom?: boolean;
  showZoomer?: boolean;
}

export interface CroppieResult {
  blob: Blob;
  objectUrl: string;
}

export interface ProcessedImage {
  blob: Blob;
  objectUrl: string;
  file: File;
}

// File validation helpers
export const validateFile = (file: File, options: { maxSizeMB: number; allowedTypes: string[] }): boolean => {
  if (file.size > options.maxSizeMB * 1024 * 1024) return false;
  if (!options.allowedTypes.includes(file.type)) return false;
  return true;
};

// Image dimension helpers
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
  });
};

// Croppie helpers
export const getViewportDimensions = (
  containerWidth: number,
  aspectRatio: number
): { width: number; height: number } => {
  const viewportSize = Math.floor(containerWidth * 0.8);
  return {
    width: viewportSize,
    height: Math.floor(viewportSize / aspectRatio)
  };
};

export const getBoundaryDimensions = (
  containerWidth: number
): { width: number; height: number } => {
  return {
    width: containerWidth,
    height: containerWidth
  };
};

export const getDefaultCroppieOptions = (
  config: CroppieConfig
): CroppieOptions => {
  const { viewport, boundary } = {
    viewport: getViewportDimensions(config.containerWidth, config.aspectRatio),
    boundary: getBoundaryDimensions(config.containerWidth)
  };

  return {
    enableExif: true,
    enableOrientation: true,
    enableZoom: config.enableZoom ?? true,
    showZoomer: config.showZoomer ?? true,
    boundary,
    viewport: {
      ...viewport,
      type: config.type
    }
  };
};

export const initCroppie = (
  container: HTMLElement,
  config: CroppieConfig,
  existingInstance?: Croppie | null
): Croppie => {
  if (existingInstance) {
    existingInstance.destroy();
  }

  const options = getDefaultCroppieOptions(config);
  return new Croppie(container, options);
};

export const getCroppieResult = async (
  croppie: Croppie,
  options: {
    type: 'blob' | 'canvas' | 'html' | 'base64';
    format?: 'jpeg' | 'png' | 'webp';
    quality?: number;
    circle?: boolean;
  }
): Promise<CroppieResult> => {
  const result = await croppie.result({
    type: options.type,
    format: options.format || 'png',
    quality: options.quality || 0.85,
    circle: options.circle
  });

  let blob: Blob;
  if (result instanceof Blob) {
    blob = result;
  } else if (result instanceof HTMLCanvasElement) {
    blob = await new Promise<Blob>((resolve, reject) => {
      result.toBlob(
        (b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create blob from canvas'));
        },
        `image/${options.format || 'png'}`,
        options.quality || 0.85
      );
    });
  } else {
    throw new Error('Unsupported result type');
  }

  const objectUrl = URL.createObjectURL(blob);
  return { blob, objectUrl };
};

// Image processing helpers
export const resizeImage = async (
  blob: Blob,
  options: ImageProcessingOptions
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(blob);
    
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      let width = img.width;
      let height = img.height;

      if (width > options.maxWidth || height > options.maxHeight) {
        const ratio = Math.min(options.maxWidth / width, options.maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (resizedBlob) => {
          if (resizedBlob) {
            resolve(resizedBlob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        `image/${options.format || 'png'}`,
        options.quality || 0.85
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
  });
};

// Combined processing function
export const processImage = async (
  file: File,
  options: {
    maxSizeMB: number;
    allowedTypes: string[];
    processing: ImageProcessingOptions;
  }
): Promise<ProcessedImage> => {
  // Validate file
  if (!validateFile(file, { maxSizeMB: options.maxSizeMB, allowedTypes: options.allowedTypes })) {
    throw new Error('Invalid file');
  }

  // Resize image
  const blob = await resizeImage(file, options.processing);
  const objectUrl = URL.createObjectURL(blob);

  return {
    blob,
    objectUrl,
    file
  };
}; 

// Compose a square social image by overlaying a PNG frame on top of a base image (e.g., user avatar)
// KISS: fixed square output, cover-crop the base to square, scale frame to fit
export const composeFrameOverImage = async (
  params: { baseImageUrl: string; frameImageUrl: string; size?: number }
): Promise<{ blob: Blob; objectUrl: string }> => {
  const { baseImageUrl, frameImageUrl, size = 1080 } = params;

  const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });

  const [baseImg, frameImg] = await Promise.all([
    loadImage(baseImageUrl),
    loadImage(frameImageUrl)
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Cover-crop base image to a square
  const shortestSide = Math.min(baseImg.width, baseImg.height);
  const sx = Math.floor((baseImg.width - shortestSide) / 2);
  const sy = Math.floor((baseImg.height - shortestSide) / 2);
  const sSize = shortestSide;

  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(baseImg, sx, sy, sSize, sSize, 0, 0, size, size);
  // Draw frame over the top, scaled to canvas
  ctx.drawImage(frameImg, 0, 0, frameImg.width, frameImg.height, 0, 0, size, size);

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to create blob'))), 'image/png', 0.95);
  });
  const objectUrl = URL.createObjectURL(blob);
  return { blob, objectUrl };
};